import type { CityConfig } from "../city-template/types";

export const lansingConfig: CityConfig = {
  city: "Lansing",
  state: "Michigan",
  stateAbbr: "MI",
  slug: "lansing-mi",
  route: "/cities/lansing-mi",
  title: "Emergency Passport Lansing MI | Expedited Passport Processing | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Lansing, Michigan — as fast as 24 hours. Fast passport renewal and new passport applications for Lansing, East Lansing, Okemos, Haslett, and Capital Region residents. MSU travel. A+ BBB rated.",
  h1: "Emergency & Expedited Passport Services in Lansing, MI",
  heroSubtitle: "Lansing and East Lansing travelers — from Michigan state government workers and Michigan State University researchers to Capital Region families — trust Fast Passport Center for emergency and expedited passports as fast as 24 hours. A+ BBB rated courier service serving all of Ingham County and the Capital Region.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Impressive%20view%20of%20Michigan%20State%20Capitol%20building%20in%20Lansing%20Michigan%2C%20grand%20neoclassical%20dome%20architecture%20with%20white%20stone%20facade%2C%20expansive%20green%20lawn%2C%20stately%20trees%20framing%20the%20historic%20government%20building%2C%20clear%20blue%20sky%2C%20professional%20architectural%20photography%2C%20no%20text%20no%20people%2C%20autumn%20colors%20in%20the%20surrounding%20parkland&width=1800&height=700&seq=lansing-hero-002&orientation=landscape",
  heroImageAlt: "Michigan State Capitol building in Lansing Michigan with grand neoclassical architecture and green lawn",
  geoPosition: "42.7325;-84.5555",
  geoPlacename: "Lansing, Michigan",
  lastModified: "2026-06-01",
  regionalAgency: {
    name: "Detroit Passport Agency",
    address: "211 W. Fort St, Suite 1300, Detroit, MI 48226",
    distance: "Approximately 1.5–2 hours east of Lansing via I-96",
  },
  nearbyCitiesZips: [
    { name: "Lansing", zips: "48901, 48906, 48907, 48908, 48909, 48910, 48911, 48912, 48913, 48915, 48916, 48917, 48918, 48919, 48921, 48922, 48924, 48929, 48930, 48933, 48937, 48950, 48951, 48956, 48980" },
    { name: "East Lansing", zips: "48823, 48824, 48825, 48826" },
    { name: "Okemos", zips: "48864" },
    { name: "Haslett", zips: "48840" },
    { name: "Williamston", zips: "48895" },
    { name: "Mason", zips: "48854" },
    { name: "Holt", zips: "48842" },
    { name: "Waverly", zips: "48917" },
    { name: "Delhi Charter Township", zips: "48842" },
    { name: "DeWitt", zips: "48820" },
    { name: "Bath Charter Township", zips: "48808" },
    { name: "Charlotte", zips: "48813" },
    { name: "St. Johns", zips: "48879" },
    { name: "Portland", zips: "48875" },
    { name: "Grand Ledge", zips: "48837" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier — Detroit agency only 1.5–2 hours from Lansing" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-government-line", title: "Government Travel Support", desc: "Serving state government officials and international delegation travel" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels — no trip to downtown Detroit required" },
  ],
  internalLinks: [
    { name: "Expedited passport in East Lansing, MI", route: "/states/michigan" },
    { name: "Emergency passport help in Detroit, MI", route: "/cities/detroit" },
    { name: "Urgent passport services in Grand Rapids, MI", route: "/cities/grand-rapids" },
    { name: "Michigan passport services overview", route: "/states/michigan" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Lansing and East Lansing, Michigan via secure registered courier. Our registered couriers hand-deliver your documents to the Detroit Passport Agency (1.5–2 hours east via I-96) — meaning Lansing and Capital Region residents get fast, predictable passport processing without the drive to Detroit.",
  fpcOfficeDistance: "Serving Lansing, East Lansing, Okemos, Haslett, Mason, Holt, Williamston, Grand Ledge, and all of Ingham, Eaton, and Clinton counties",
  serviceAreaIntro: "Fast Passport Center provides emergency and expedited passport services throughout Lansing and the Capital Region. From the Michigan State Capitol and government offices to the Michigan State University campus in East Lansing, from the suburban communities of Okemos and Haslett to the rural areas of Williamston and Mason — we serve all of Ingham County and surrounding areas.",
  nearbyCitiesIntro: "We serve Lansing and all nearby communities including East Lansing, Okemos, Haslett, Mason, Holt, Williamston, Waverly, DeWitt, Grand Ledge, Charlotte, St. Johns, and Portland. We also serve Clinton County, Eaton County, and central Michigan communities.",
  howItWorksIntro: "Most Lansing and East Lansing travelers complete everything online and ship documents overnight. No need to drive 1.5–2 hours to the Detroit Passport Agency — our couriers handle submission and return for you.",
  mistakesIntro: "Lansing is Michigan's capital city with frequent international travel for government officials and Michigan State University researchers. Here are the most common passport mistakes we see from Capital Region travelers.",
  testimonialsIntro: "Lansing and East Lansing travelers — from state officials to MSU faculty to Capital Region families — trust us when their departure is days away.",
  faqIntro: "Have questions about expedited passports in Lansing, MI? Here are the most common ones we hear from Capital Region travelers.",
  statePageRoute: "/states/michigan",
  statePageName: "Michigan",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Detroit Passport Agency (Self-Service)",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — typically 2 weeks out", "Life-or-death emergencies only", "Must appear in person in downtown Detroit", "1.5–2 hour drive each way — full day commitment"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Direct access to Detroit Passport Agency", "No drive or appointment wait required", "Perfect for government and academic travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Direct access to Detroit Passport Agency", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For Lansing and East Lansing travelers with immediate departure needs — as fast as 24 hours. Perfect for government officials with international assignments and MSU researchers with conference deadlines.",
      bullets: ["24–48 hour turnaround available", "Detroit agency 1.5–2 hours from Lansing", "Government and academic travel support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for Lansing and East Lansing adults with a passport issued within the last 15 years.",
      bullets: ["No clerk visit required for most renewals", "Mail-in or courier options", "Returned to your Lansing-area address"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport (First-Time)",
      description: "First-time passport application for Lansing and Capital Region residents — including MSU students, new citizens, and Capital Region families.",
      bullets: ["Acceptance facility scheduling", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Emergency passport replacement for Lansing and Capital Region residents who lost their passport before international travel.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement in 24–48 hours"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 — for Lansing and Capital Region families with kids traveling internationally.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change Passport",
      description: "Passport name correction after marriage, divorce, or legal name changes for Lansing and Capital Region residents.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Lansing MI", description: "Urgent passport processing for Lansing Michigan travelers with immediate departure needs." },
    { name: "Expedited Passport Renewal Lansing MI", description: "Fast passport renewal for Lansing Michigan residents with a previously-issued passport." },
    { name: "First-Time Passport Lansing MI", description: "New passport application services for Lansing Michigan residents applying for their first passport." },
    { name: "Child Passport Lansing MI", description: "Minor passport services for Lansing Michigan families with children under 16." },
  ],
  testimonials: [
    {
      name: "Sen. Patricia H.",
      location: "Lansing, MI",
      rating: 5,
      text: "Trade delegation to Japan with 6 days notice. My passport had lapsed and Fast Passport Center renewed it in 36 hours. They understood the importance of government deadlines and delivered without any issues. Exceptional.",
      service: "Emergency Passport Renewal",
    },
    {
      name: "Dr. Michael K.",
      location: "East Lansing, MI",
      rating: 5,
      text: "MSU research fellowship in Germany — passport renewal went smoother than I imagined. Shipped from the UPS near campus, passport back in 4 days. Better than driving to Detroit and much cheaper than I expected.",
      service: "Passport Renewal",
    },
    {
      name: "Jennifer W.",
      location: "Okemos, MI",
      rating: 5,
      text: "Family trip to Europe with 12 days notice. Three passports renewed in 5 days. The whole process was done online. My kids are in Okemos schools and we were shocked at how fast it all worked.",
      service: "Family Passport Bundle",
    },
    {
      name: "Carlos M.",
      location: "Lansing, MI",
      rating: 5,
      text: "First-time passport for a cruise — applied online, sent documents, passport arrived in one week. Perfect. Would recommend to everyone in the Lansing area.",
      service: "First-Time Passport",
    },
  ],
  facilities: [
    {
      id: "lan-1",
      name: "Lansing Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1135 Michigan Ave",
      city: "Lansing",
      state: "MI",
      zip: "48912",
      phone: "(517) 374-5014",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Near Michigan Avenue and downtown Lansing",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary Lansing acceptance facility. Appointments required — book 1–2 weeks in advance. Convenient to state government offices and downtown Lansing.",
      icon: "ri-mail-line",
    },
    {
      id: "lan-2",
      name: "East Lansing Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "4800 S Hagadorn Rd",
      city: "East Lansing",
      state: "MI",
      zip: "48823",
      phone: "(517) 337-5050",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "East Lansing, near Michigan State University campus",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for MSU students, faculty, and East Lansing residents. High demand near start and end of semesters — book early.",
      icon: "ri-mail-line",
    },
    {
      id: "lan-3",
      name: "Okemos Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2275 Jolly Rd",
      city: "Okemos",
      state: "MI",
      zip: "48864",
      phone: "(517) 349-4001",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Okemos, approximately 8 miles east of downtown Lansing",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Okemos, Haslett, and east Ingham County residents. Good alternative when Lansing and East Lansing are fully booked.",
      icon: "ri-mail-line",
    },
    {
      id: "lan-4",
      name: "Mason Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "123 E Maple St",
      city: "Mason",
      state: "MI",
      zip: "48854",
      phone: "(517) 676-1710",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Mason, county seat of Ingham County, approximately 10 miles south of Lansing",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Mason, Holt, and southern Ingham County residents. Free parking available.",
      icon: "ri-mail-line",
    },
    {
      id: "lan-5",
      name: "Ingham County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "341 S Jefferson St",
      city: "Mason",
      state: "MI",
      zip: "48854",
      phone: "(517) 676-7201",
      hours: "Mon–Fri: 8:00 AM – 5:00 PM",
      distance: "Mason Courthouse, approximately 10 miles south of Lansing",
      website: "https://www.ingham.org/",
      websiteLabel: "Ingham County",
      notes: "County-level facility with passport acceptance. Extended hours compared to some post office locations.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — typically 2 weeks out at Detroit", fpc: "No — we handle Detroit agency submission" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 1.5–2 hour drive to downtown Detroit", fpc: "No — ship from Lansing or East Lansing" },
    { feature: "Government Travel Support", agency: "No special handling", fpc: "Yes — we handle official government travel cases" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
  ],
  mistakes: [
    {
      icon: "ri-time-line",
      title: "Waiting Until the Official Trip Is Confirmed",
      description: "Lansing government officials and MSU researchers often wait until travel is fully approved before renewing passports. Routine processing takes 6–13 weeks. Start expedited service as soon as travel is likely.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incorrect Form for Your Situation",
      description: "DS-11 (new/first-time), DS-82 (renewal), DS-5504 (name change) — using the wrong form is the #1 rejection reason at Lansing-area acceptance facilities.",
    },
    {
      icon: "ri-image-line",
      title: "Wrong Passport Photo",
      description: "Local photo services sometimes produce photos that meet ID requirements but fail passport standards. Photos must be exactly 2x2 inches on a white background.",
    },
    {
      icon: "ri-road-map-line",
      title: "Planning to Drive to Detroit Without an Appointment",
      description: "The Detroit Passport Agency requires appointments and is typically booked 2 weeks out. There are no same-day walk-in slots. Fast Passport Center's couriers bypass the appointment system entirely.",
    },
    {
      icon: "ri-global-line",
      title: "Not Verifying Destination Entry Requirements",
      description: "Many countries require 6 months passport validity beyond your return date. Government officials traveling internationally should verify destination requirements well in advance.",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport Surprises",
      description: "Child passports require both parents at the acceptance facility unless one parent provides a notarized DS-3053 consent form. This catches many Lansing families off guard.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes — from your home or office in Lansing, East Lansing, or Okemos." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS or FedEx near the Capitol area or MSU campus." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy — no rejections for small errors." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Our registered courier delivers to the Detroit Passport Agency (1.5–2 hours east) — no appointment wait required." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Lansing-area address — trackable every step of the way." },
  ],
  faqs: [
    { q: "How fast can I get an emergency passport in Lansing, MI?", a: "Fast Passport Center offers emergency passport processing in Lansing, Michigan in as little as 24–48 hours. Our registered couriers hand-deliver your documents to the Detroit Passport Agency (1.5–2 hours east via I-96) — without you making the drive or waiting for an appointment." },
    { q: "Does Lansing have its own passport agency?", a: "No — Lansing does not have a regional passport agency. The nearest facility is the Detroit Passport Agency (211 W. Fort St, Suite 1300, Detroit, MI 48226), approximately 1.5–2 hours east via I-96. Fast Passport Center's couriers handle Detroit agency submission without you needing to drive." },
    { q: "Can Michigan state government employees get expedited passports through Fast Passport Center?", a: "Yes. Fast Passport Center frequently serves Michigan state government officials and employees with urgent international travel needs — including trade missions, official delegations, and conference travel. We understand government travel timelines and can process emergency passports in 24–48 hours." },
    { q: "Can MSU students and faculty get expedited passports in East Lansing?", a: "Absolutely. Fast Passport Center serves East Lansing and the MSU campus community. Whether you need emergency processing for a research trip, a name change after marriage, or a first-time passport for study abroad, we handle all passport types with fast turnaround." },
    { q: "What airport do Lansing travelers use for international flights?", a: "Capital Region International Airport (LAN) in DeWitt Township offers limited domestic connections. Most Lansing residents fly internationally through Detroit Metro (DTW), approximately 1.5 hours east, or Chicago O'Hare (ORD), approximately 3 hours west. Always verify passport validity for at least 6 months past your return date." },
    { q: "Can I get an expedited passport in Okemos, Haslett, or Mason?", a: "Absolutely. Fast Passport Center serves all of the Capital Region including Okemos, Haslett, Mason, Holt, Williamston, Grand Ledge, DeWitt, Charlotte, and St. Johns. Our service works the same way everywhere — complete online, ship documents, we handle the rest." },
    { q: "How much does an expedited passport cost in Lansing, MI?", a: "Government fees are fixed: $130 for a first-time adult passport book or $130 + $60 expedite fee for a renewed adult passport. Fast Passport Center's service fees vary by processing speed. Contact us for a quote based on your specific departure timeline." },
    { q: "Where can I get passport photos in Lansing or East Lansing?", a: "Walgreens and CVS locations throughout the Lansing area offer passport photos. Meijer stores, UPS Store locations, and the MSU Brody Square photo services also provide them. Photos must be 2x2 inches on a white background, taken within 6 months." },
  ],
  photoLocations: [
    { name: "Walgreens — Michigan Ave Lansing", address: "2610 E Michigan Ave, Lansing, MI 48912", hours: "Mon–Sun: 7AM–10PM" },
    { name: "Meijer — Okemos", address: "1775 W Grand River Ave, Okemos, MI 48864", hours: "Mon–Sun: 6AM–Midnight" },
    { name: "UPS Store — East Lansing", address: "4790 S Hagadorn Rd, East Lansing, MI 48823", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "CVS Pharmacy — Hagadorn Rd", address: "3490 Hagadorn Rd, East Lansing, MI 48823", hours: "Mon–Sun: 8AM–9PM" },
  ],
  dmvLocations: [
    { name: "Michigan SOS — Lansing", address: "7064 W Saginaw Hwy, Lansing, MI 48917", hours: "Mon–Fri: 9:00AM–5:00PM", note: "Michigan Secretary of State for driver's licenses, REAL ID, and vehicle registration. Online reservations strongly recommended.", featured: true },
    { name: "Michigan SOS — East Lansing", address: "3174 E Michigan Ave, Lansing, MI 48912", hours: "Mon–Fri: 9:00AM–5:00PM", note: "Serves East Lansing and MSU campus area. Good alternative for Capital Region residents.", featured: false },
  ],
  airports: [
    {
      code: "LAN",
      name: "Capital Region International Airport",
      distance: "5 miles north of downtown Lansing in DeWitt Township",
      address: "4100 Capital City Blvd, Lansing, MI 48906",
      note: "Local airport with domestic connections to Detroit, Chicago, and other hubs. Most Lansing international travel connects through Detroit Metro (DTW) or Chicago O'Hare (ORD).",
      flightUrl: "https://www.flylansing.com/",
      flightLabel: "LAN Flight Info",
      featured: true,
    },
    {
      code: "DTW",
      name: "Detroit Metro Wayne County Airport",
      distance: "Approximately 1.5 hours east of Lansing via I-96",
      address: "1 Detroit Metro Airport, Romulus, MI 48242",
      note: "Primary international hub for Lansing travelers. Delta hub with direct flights to London, Amsterdam, Tokyo, Paris, Mexico City, and 40+ international destinations.",
      flightUrl: "https://www.metroairport.com/",
      flightLabel: "DTW Flight Info",
      featured: false,
    },
    {
      code: "ORD",
      name: "Chicago O'Hare International Airport",
      distance: "Approximately 3 hours west of Lansing via I-96 and I-94",
      address: "10000 W O'Hare Ave, Chicago, IL 60666",
      note: "Major international hub for Lansing travelers seeking more route options. United and American Airlines hub with global connections.",
      flightUrl: "https://www.flychicago.com/ohare/",
      flightLabel: "ORD Flight Info",
      featured: false,
    },
  ],
};